/**
 * PB-Profil — 9 Form-Sections
 * Layout 1:1 nach dem Claude-Design (senihelp24-design-system) +
 * Daten-Quellen aus DB (`profile_options`) bzw. hardcoded (siehe options.ts).
 *
 * i18n: alle UI-Labels/Hints/Beschreibungen über react-i18next
 * (Namespace `pflege.form.*`). Format-Platzhalter (Nummern, IBAN, Telefon)
 * bleiben sprachneutral hardcodiert.
 *
 * WP5 — Field-group permissions:
 *   Each of the four protected sections (vertrag, versicherung, bank, status)
 *   is gated by pb.section.<name>.read / pb.section.<name>.write.
 *   - canRead=false  → section is hidden entirely (no DOM, no nav item).
 *   - canRead=true, canWrite=false → section renders inside a <fieldset disabled>
 *     so all controls are visually and interactively read-only.
 *   - canRead=true, canWrite=true → normal edit mode (unchanged behaviour).
 *
 *   Consumers pass a `sectionPerms` prop with per-section can{Read,Write} flags.
 *   When omitted (e.g. FamilyMemberForm for non-care-recipient), all sections
 *   default to readable+writable.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import {
  Section, GroupLabel, Grid, Field, Text, Area, Select, CheckGroup,
  ToggleRow, Range, ReadOnly, Rating, PlainText, PhoneInput,
  ActionStatus, HistoryTable, asMonth, asEuro,
  type SelectOption as SelectOptionType,
} from './controls';
import { HARDCODED, fieldOptions, labelOptions, nationalitaetOptions } from './options';
import { v1Users, type V1ProfileOption, type V1User } from '../../../lib/v1';
import { Button } from '../../../components/ui';
import { getThemeColors } from '../../../lib/theme';
import { TERMIN_TIME_FIELDS, toTimeInput, type TerminDateField, type TerminField } from '../../../lib/terminFields';
import { formRequiredContext, isRequiredField } from './validation';
import { registerPendingVertrag } from './pendingVertrag';
import { v2PriceBonus, priceBonusErrorMessage } from '../../../lib/priceBonus';
import { isSectionActive, type ToggleSectionId } from './sectionToggles';
import type { SectionToggle } from './controls';
import type { ContentTranslations } from '../../../lib/contentTranslations';
import { TranslatedText } from '../../../components/translation/TranslatedText';
import { TranslationHint } from '../../../components/translation/TranslationHint';
import { DateTimeInput, splitDateTime } from '../../../components/DateTimeInput';
import { formatDate } from '../../../lib/dateFormat';

/**
 * WP5 — Per-section read/write access flags for the four gated sections.
 *
 * Absent → defaults to { canRead: true, canWrite: true } (full access).
 * Use `deriveSectionPerms(hasPermission)` in PbProfileEdit to build this
 * from the live permissions hook.
 */
export interface SectionPerm {
  canRead: boolean;
  canWrite: boolean;
}

export interface SectionPerms {
  vertrag:      SectionPerm;
  versicherung: SectionPerm;
  bank:         SectionPerm;
  status:       SectionPerm;
}

/** Unrestricted defaults — used when sectionPerms is omitted. */
const FULL_ACCESS: SectionPerm = { canRead: true, canWrite: true };

const FULL_PERMS: SectionPerms = {
  vertrag:      FULL_ACCESS,
  versicherung: FULL_ACCESS,
  bank:         FULL_ACCESS,
  status:       FULL_ACCESS,
};

/**
 * Build a SectionPerms object from the `hasPermission` hook result.
 *
 * Usage in PbProfileEdit:
 *   const { hasPermission } = usePermissions();
 *   const sectionPerms = deriveSectionPerms(hasPermission);
 */
export function deriveSectionPerms(hasPermission: (key: string) => boolean): SectionPerms {
  const sp = (name: string): SectionPerm => ({
    canRead:  hasPermission(`pb.section.${name}.read`),
    canWrite: hasPermission(`pb.section.${name}.write`),
  });
  return {
    vertrag:      sp('vertrag'),
    versicherung: sp('versicherung'),
    bank:         sp('bank'),
    status:       sp('status'),
  };
}

/**
 * Wraps children in a disabled fieldset when canWrite=false, making all form
 * controls visually and interactively read-only without altering each control.
 * The fieldset itself has no visual border; styling is provided by the Section.
 */
function ReadOnlyWrapper({ canWrite, children }: { canWrite: boolean; children: React.ReactNode }) {
  if (canWrite) return <>{children}</>;
  return (
    <fieldset
      disabled
      style={{
        all: 'unset',
        display: 'contents',
        opacity: 0.72,
        cursor: 'default',
      }}
    >
      {children}
    </fieldset>
  );
}

export interface PbFormState {
  // Familienkonto — Profil-Kopfblock (Rolle/Familiengrad/Haushalt/Pflegegrad).
  // profiltyp trägt den Options-Wert (id-als-String) der profiltyp-Gruppe;
  // familiengrad ist ein Array (Multi-Select); haushalt = „lebt im Haushalt";
  // pflegegrad_level = kein('')/1–5 (getrennt vom pflege-Sektions-`pflegegrad`,
  // das den Pflege-Options-Wert trägt).
  // is_pflegeempfaenger = expliziter Toggle — steuert schlanke vs. volle Sicht.
  profiltyp?: string; familiengrad?: string[]; haushalt?: boolean; pflegegrad_level?: string;
  /** Feedback 11.10.2026 — alle Rollen (Mehrfachauswahl); profiltyp = primäre Rolle. */
  profiltypen?: string[];
  is_pflegeempfaenger?: boolean;
  // header / lifecycle
  status?: string; konto?: string; startdatum?: string;
  // Phasen-Regelsystem — Bedingungs-Datumsfelder
  zusage_erhalten_am?: string; mustervertrag_versendet_am?: string; anamnese_termin_am?: string;
  beratungsgespraech_am?: string;
  /** Uhrzeit (HH:MM) zu Anamnese-Termin / Beratungsgespräch — leer = ganztägig. */
  anamnese_termin_um?: string; beratungsgespraech_um?: string;
  assigned_to?: string; group_id?: string; locale?: string;
  badge?: string; impfstatus?: string;
  rating?: number; german_skills?: string; description?: string;
  invisible?: boolean; created_by?: string; origin?: string;
  /** Aufgelöster Name zu created_by — die rohe ID sagt niemandem etwas. */
  created_by_name?: string;
  // stammdaten
  anrede?: string; titel?: string; vorname?: string; nachname?: string;
  geburtsdatum?: string; nationalitaet?: string;
  groesse?: string; gewicht?: string; familienstand?: string; raucher?: string;
  // versicherung
  krankenkasse?: string; krankenkasse_typ?: string;
  kranken_vnr?: string; sozial_vnr?: string; renten_vnr?: string;
  steuer_id?: string; lbnr?: string; sozialamt_aufstockung?: boolean;
  // pflegedienst (Batch-12) — SGB5-Dropdown + bedingte Felder
  sgb5?: string; sgb5_medigabe?: boolean; sgb5_medigabe_taeglich?: string; sgb5_ats?: boolean;
  // pflege
  pflegegrad?: string; beweglichkeit?: string;
  einschraenkungen?: string[]; sonstige_einschraenkungen?: string;
  besserung?: string; sinneswahrnehmung?: string[];
  geisteszustand?: string; schlafverhalten?: string;
  grundpflege?: string; hygiene?: string; ankleiden?: string;
  essen?: string; urinkontrolle?: string; stuhlkontrolle?: string;
  // wohnen
  wohnsituation?: string; wohnort?: string; wohnzustand?: string[];
  haustiere?: string; haustiere_welche?: string[]; einkaufsmoeglichkeiten?: string;
  charaktereigenschaften?: string[]; ausgepraegtes_familienleben?: boolean;
  // wunsch-bk
  bk_anrede?: string[]; bk_wohnen?: string[]; bk_aktivitaeten?: string[];
  bk_deutsch?: string; bk_fuehrerschein?: string; bk_raucher?: string;
  // freitexte
  hobbys?: string; umfeld?: string; sonstiges?: string;
  // SIS (Strukturiertes Informationssystem) — 9 Freitextfelder
  freitext_sis_1?: string; freitext_sis_2?: string; freitext_sis_3?: string;
  freitext_sis_4?: string; freitext_sis_5?: string; freitext_sis_6?: string;
  freitext_sis_7?: string; freitext_sis_8?: string; freitext_sis_9?: string;
  /** Schalter der Sektionen 02–07 (s. sectionToggles.ts); fehlender Key = aus Daten abgeleitet. */
  sektionen_aktiv?: Partial<Record<ToggleSectionId, boolean>>;
  // adresse
  email?: string; mobile?: string; telefon?: string;
  strasse?: string; hausnummer?: string; plz?: string; ort?: string; land?: string;
  // vertrag — Vertragsart/-preis und Ausgleichsbetrag sind eigene Datensätze
  // (prices_pb / price_bonus, s. PbVertragBlock); im Formular nur der Nettolohn.
  wage?: string;
  // bank / status
  kontoinhaber?: string; bankname?: string; iban?: string;
  todestag?: string;
  /** Nur lesend in der Termine-Sektion — gepflegt über eine eigene Wiedervorlage-Maske. */
  wiedervorlage_am?: string | null;
}

// Navigations-Struktur — Labels werden zur Laufzeit über t() aufgelöst.
export const SECTIONS_NAV = [
  { groupKey: 'pflege.form.group.profile', items: [
    { id: 'profil',       num: '00', labelKey: 'pflege.form.section.profil.nav' },
    { id: 'stammdaten',   num: '01', labelKey: 'pflege.form.section.stammdaten.nav' },
    { id: 'versicherung', num: '02', labelKey: 'pflege.form.section.versicherung.nav' },
    { id: 'pflege',       num: '03', labelKey: 'pflege.form.section.pflege.nav' },
    { id: 'charakter',    num: '04', labelKey: 'pflege.form.section.charakter.nav' },
    { id: 'wunsch',       num: '05', labelKey: 'pflege.form.section.wunsch.nav' },
    { id: 'freitexte',    num: '06', labelKey: 'pflege.form.section.freitexte.nav' },
    { id: 'sis',          num: '07', labelKey: 'pflege.form.section.sis.nav' },
    { id: 'termine',      num: '08', labelKey: 'pflege.form.section.termine.nav' },
  ]},
  { groupKey: 'pflege.form.group.contact', items: [
    { id: 'vertrag', num: '09', labelKey: 'pflege.form.section.vertrag.nav' },
  ]},
  { groupKey: 'pflege.form.group.admin', items: [
    { id: 'status',    num: '10', labelKey: 'pflege.form.section.status.nav' },
    { id: 'bank',      num: '11', labelKey: 'pflege.form.section.bank.nav' },
    { id: 'sonstiges', num: '12', labelKey: 'pflege.form.section.sonstiges.nav' },
  ]},
] as const;

interface Props {
  f: PbFormState;
  set: <K extends keyof PbFormState>(key: K, val: PbFormState[K]) => void;
  opts: Record<string, V1ProfileOption[]> | null;
  dark: boolean;
  /** WP4: fields that failed validation — receives red outline + label. */
  invalidFields?: Array<keyof PbFormState>;
  /**
   * WP5: per-section read/write flags for the four gated sections.
   * Omit (or pass undefined) to grant full access (Administrator default).
   */
  sectionPerms?: SectionPerms;
}

/**
 * Paket A (A8): Rolle „Pflegeempfänger/in". Die profile_options-Gruppe
 * `profiltyp` (Seed 2026_07_06_100004) kennt nur Frau/Mann/Rechnungsempfänger/
 * Hausarzt/Kontaktperson. Die Rolle wird deshalb hier als Frontend-Konstante
 * an die Liste angehängt, falls sie nicht vom Backend kommt. Das v2-Backend
 * validiert profiltyp gegen profile_options.value → ein Backend-Seed für
 * diesen Wert ist nötig, sonst lehnt der Server das Speichern ab.
 * Die Auswahl dieser Rolle schaltet `is_pflegeempfaenger` automatisch ein.
 */
export const PFLEGEEMPFAENGER_ROLE = 'Pflegeempfänger/in';

/** Gewählte Rollen; Altbestand ohne profiltypen → [profiltyp]. */
export function selectedRoles(f: Pick<PbFormState, 'profiltyp' | 'profiltypen'>): string[] {
  const list = Array.isArray(f.profiltypen) ? f.profiltypen.filter(Boolean) : [];
  if (f.profiltyp && !list.includes(f.profiltyp)) return [f.profiltyp, ...list];
  return list;
}

/** Primäre Rolle: Pflegeempfänger-Rolle (Frau/Mann bzw. „Pflegeempfänger/in") zuerst, sonst die erste. */
export function primaryRole(roles: string[], options: SelectOptionType[] = []): string | null {
  const labelOf = (v: string) => options.find(o => o.value === v)?.label ?? v;
  const care = roles.find(v => ['Frau', 'Mann', PFLEGEEMPFAENGER_ROLE].includes(labelOf(v)) || ['Frau', 'Mann', PFLEGEEMPFAENGER_ROLE].includes(v));
  return care ?? roles[0] ?? null;
}

export function withPflegeempfaengerRole(
  options: SelectOptionType[],
  tr: (key: string, opts?: Record<string, unknown>) => string = (k, opts) => i18n.t(k, opts),
): SelectOptionType[] {
  const has = options.some(o => o.value === PFLEGEEMPFAENGER_ROLE || o.label === PFLEGEEMPFAENGER_ROLE);
  // `value` MUSS der Rohwert bleiben (Backend validiert profiltyp gegen
  // profile_options.value) — nur das angezeigte `label` wird übersetzt.
  return has ? options : [...options, { value: PFLEGEEMPFAENGER_ROLE, label: tr('pflege.form.pflegeempfaenger_role_label') }];
}

/* ─── Familienkonto: Profil-Kopfblock ──────────────────────────────────────
 *
 * Rolle (Profiltyp, Pflicht) · Familiengrad · Haushalt (Ja/Nein) · Pflegegrad
 * (kein/1–5). Die Optionslisten kommen LIVE aus `v1ProfileOptions.grouped()`
 * (Gruppen `profiltyp` und `familiengrad`) — nicht hartkodiert. Der Pflegegrad
 * ist die feste kein/1–5-Auswahl (PFLEGEGRAD_OPTIONS).
 */
export function ProfilHeadSection({
  f, set, opts, dark, num = '00',
  profiltypOptions: profiltypOptionsProp,
  familiengradOptions: familiengradOptionsProp,
  showPflegegrad = true,
  invalidFields,
}: Props & {
  num?: string;
  /**
   * Optionen für Rolle/Familiengrad. Im Familienkonto-Kontext MUSS das LABEL
   * der `value` sein (das v2-Backend validiert profiltyp/familiengrad gegen
   * `profile_options.value`, nicht gegen die ID). Werden keine Options
   * übergeben, fällt die Sektion auf das geteilte `fieldOptions` zurück
   * (ID-als-value) — für Nicht-Familienkonto-Nutzung.
   */
  profiltypOptions?: SelectOptionType[];
  familiengradOptions?: SelectOptionType[];
  /**
   * Ob der (kein/1–5)-Pflegegrad-Selektor gezeigt wird. In `PbProfileEdit`
   * (Profildaten-Tab) FALSE, weil die Pflege-Sektion bereits ein Options-
   * basiertes Pflegegrad-Control hat — der Kopfblock würde es sonst doppeln.
   * Im „+ Person hinzufügen"-Schnellformular TRUE.
   */
  showPflegegrad?: boolean;
}) {
  const { t: tr } = useTranslation();
  const profiltypOptions = withPflegeempfaengerRole(profiltypOptionsProp ?? fieldOptions('profiltyp', opts, f.profiltyp), tr);
  const familiengradOptions = familiengradOptionsProp ?? fieldOptions('familiengrad', opts);
  // A8: Rolle „Pflegeempfänger/in" aktiviert den Pflegeempfänger-Schalter
  // und „Ja, im Haushalt" (die Person lebt in ihrem eigenen Haushalt).
  // Nur einschalten, nie ausschalten — der Operator kann beides danach ändern.
  const isPflegeempfaengerRole = (v: string) =>
    v === PFLEGEEMPFAENGER_ROLE
    || profiltypOptions.some(o => o.value === v && o.label === PFLEGEEMPFAENGER_ROLE);
  // Feedback 11.10.2026 — Rolle als Mehrfachauswahl. profiltyp bleibt die
  // primäre Rolle (Pflicht, Backend-Bestandslogik): Pflegeempfänger-Rolle
  // zuerst, sonst die erste gewählte.
  const roles = selectedRoles(f);
  const onRoles = (next: string[]) => {
    const primary = primaryRole(next, profiltypOptions);
    // Primäre Rolle vorn — das Backend nimmt ohne Frau/Mann die erste.
    set('profiltypen', primary ? [primary, ...next.filter(v => v !== primary)] : next);
    set('profiltyp', primary ?? '');
    const added = next.filter(v => !roles.includes(v));
    if (added.some(isPflegeempfaengerRole)) {
      if (!f.is_pflegeempfaenger) set('is_pflegeempfaenger', true);
      if (!f.haushalt) set('haushalt', true);
    }
  };
  const isInvalid = (key: keyof PbFormState) => (invalidFields ?? []).includes(key);
  // Sternchen kommt aus validation.ts — nicht hartkodiert, s. requiredFields().
  const req = (key: keyof PbFormState) => isRequiredField(f, key);
  return (
    <Section
      dark={dark}
      id="profil"
      num={num}
      title={tr('pflege.form.section.profil.title')}
      desc={tr('pflege.form.section.profil.desc')}
    >
      <Grid cols={showPflegegrad ? 2 : 1}>
        <Field dark={dark} required={req('profiltyp')} label={tr('pflege.form.field.profiltyp')} hint={tr('pflege.form.hint.multi')} hasError={isInvalid('profiltyp')}>
          <div data-testid="profiltyp-multi">
            <CheckGroup
              dark={dark}
              value={roles}
              onChange={onRoles}
              options={profiltypOptions}
              cols={2}
            />
          </div>
        </Field>
        {showPflegegrad && (
          <Field dark={dark} label={tr('pflege.form.field.pflegegrad_level')}>
            <Select
              dark={dark}
              value={f.pflegegrad_level ?? ''}
              onChange={v => set('pflegegrad_level', v)}
              options={PFLEGEGRAD_LEVEL_OPTIONS.map(o => ({ value: o.value, label: tr(o.labelKey) }))}
            />
          </Field>
        )}
      </Grid>
      {/* Batch-12: Familiengrad nur für Kontakte — sobald die Person als
          Pflegeempfänger aktiviert ist, wird das Feld ausgeblendet. */}
      {!f.is_pflegeempfaenger && (
        <div style={{ marginTop: 16 }}>
          <Field dark={dark} label={tr('pflege.form.field.familiengrad')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup
              dark={dark}
              value={f.familiengrad ?? []}
              onChange={v => set('familiengrad', v)}
              options={familiengradOptions}
              cols={3}
            />
          </Field>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <Grid cols={2}>
          <Field dark={dark} label={tr('pflege.form.field.haushalt')}>
            <ToggleRow
              dark={dark}
              value={f.haushalt ?? false}
              onChange={v => set('haushalt', v)}
              label={tr('pflege.form.toggle.haushalt_short')}
              desc={tr('pflege.form.toggle.haushalt_desc')}
            />
          </Field>
          <Field dark={dark} label={tr('pflege.familienkonto.detail.role_care_recipient')}>
            <ToggleRow
              dark={dark}
              value={f.is_pflegeempfaenger ?? false}
              onChange={v => set('is_pflegeempfaenger', v)}
              label={tr('pflege.familienkonto.detail.role_care_recipient')}
              desc={tr('pflege.form.toggle.is_pflegeempfaenger_desc')}
            />
          </Field>
        </Grid>
      </div>
    </Section>
  );
}

/** Pflegegrad-Auswahl (kein/1–5) — i18n-Keys, damit UI-Sprache greift. */
const PFLEGEGRAD_LEVEL_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: '',  labelKey: 'pflege.form.pflegegrad.none' },
  { value: '1', labelKey: 'pflege.form.pflegegrad.pg1' },
  { value: '2', labelKey: 'pflege.form.pflegegrad.pg2' },
  { value: '3', labelKey: 'pflege.form.pflegegrad.pg3' },
  { value: '4', labelKey: 'pflege.form.pflegegrad.pg4' },
  { value: '5', labelKey: 'pflege.form.pflegegrad.pg5' },
];

/** Paket A (A9): Sektionen der schlanken Sicht (Nicht-Pflegeempfänger). */
export const SLIM_SECTION_IDS: ReadonlyArray<string> = ['profil', 'stammdaten'];

export function FormSections({
  f, set, opts, dark, showProfilHead = false, invalidFields, sectionPerms,
  slim = false, freitexteReadOnly = false, profileId, onSaveTermin, priceUser,
  contentTranslations, translationIds,
}: Props & {
  /**
   * Paket A (A9): Nicht-Pflegeempfänger sehen nur „Profil" + „Stammdaten".
   * Alle weiteren Sektionen werden nicht gerendert.
   */
  slim?: boolean;
  /**
   * Paket A (A5): Sektion „06 Freitexte" nur als Text ausgeben (kein
   * textarea). Wird von /profildaten gesetzt; alle anderen Sektionen bleiben
   * unverändert editierbar.
   */
  freitexteReadOnly?: boolean;
  /**
   * Blendet den Familienkonto-Kopfblock „Profil" (Rolle/Familiengrad/
   * Haushalt) VOR den Standard-Sektionen ein. Der (kein/1–5)-Pflegegrad wird
   * hier bewusst NICHT gezeigt — die Pflege-Sektion trägt bereits ein Options-
   * basiertes Pflegegrad-Control. profiltyp/familiengrad nutzen Label-Optionen
   * (Backend-Vertrag = Label).
   */
  showProfilHead?: boolean;
  /**
   * Profil-ID für den Termine-Save (PUT /v2/admin/profiles/{id}). Ohne ID
   * (z. B. „Person hinzufügen" im Familienkonto — die Person existiert noch
   * nicht) rendert die Termine-Sektion nicht, weil es noch nichts zu
   * speichern gibt.
   */
  profileId?: number | null;
  /**
   * Speichert `anamnese_termin_am`/`beratungsgespraech_am` (+ Uhrzeit) sofort (wie das
   * Muster im PhaseRulesBlock), NICHT über den großen Profildaten-Save-Button
   * — sonst würde ein Klick auf „Profil speichern" mit einem veralteten
   * Formularstand den gerade gesetzten Termin überschreiben.
   */
  onSaveTermin?: (patch: Partial<Record<TerminField, string | null>>) => Promise<void>;
  /**
   * User mit `prices`/`priceCurrent`/`boni` (GET /v1/admin/user/{id}) für
   * Vertragsanpassung und Ausgleichsbetrag. Ohne (Person wird gerade erst
   * angelegt) zeigt die Vertrag-Sektion nur den Nettolohn.
   */
  priceUser?: V1User | null;
  /** KI-Übersetzung TP1: mehrsprachige Inhalte (aus GET /admin/user/{id}). */
  contentTranslations?: ContentTranslations | null;
  /** IDs der übersetzten Objekte — ohne sie keine Anzeige/Korrektur. */
  translationIds?: { profile: number | null; user: number | null };
}) {
  const { t: tr } = useTranslation();

  // WP5: resolve effective perms (fall back to full access when not provided)
  const perms: SectionPerms = sectionPerms ?? FULL_PERMS;

  // WP4: helper to check if a field has a validation error
  const isInvalid = (key: keyof PbFormState) => (invalidFields ?? []).includes(key);
  // Pflichtfeld-Sternchen: einzige Quelle ist validation.ts (requiredFields),
  // damit `*` und die Prüfung beim Speichern nicht auseinanderlaufen. Die
  // Pflege-Pflichtfelder hängen an is_pflegeempfaenger und verschwinden bei
  // Kontaktpersonen automatisch wieder.
  const reqCtx = formRequiredContext({ slim, perms, freitexteReadOnly });
  const req = (key: keyof PbFormState) => isRequiredField(f, key, reqCtx);
  // Pflicht-Markierung + roter Rahmen für Felder der Sektionen 02–07.
  const rq = (key: keyof PbFormState) => ({ required: req(key), hasError: isInvalid(key) });

  // shorthand: binds value+onChange for a string field
  const b = <K extends keyof PbFormState>(key: K) => ({
    value: f[key] as string | undefined,
    onChange: (v: string) => set(key, v as PbFormState[K]),
  });
  const bMulti = <K extends keyof PbFormState>(key: K) => ({
    value: (f[key] as string[] | undefined) ?? [],
    onChange: (v: string[]) => set(key, v as PbFormState[K]),
  });
  const bBool = <K extends keyof PbFormState>(key: K) => ({
    value: f[key] as boolean | undefined,
    onChange: (v: boolean) => set(key, v as PbFormState[K]),
  });

  const opt = (k: string) => fieldOptions(k, opts);
  const fl = (k: string) => tr(`pflege.form.field.${k}`);

  // KI-Übersetzung: Vorschau unter editierbaren übersetzbaren Feldern.
  const hint = (model: 'profile' | 'user', field: keyof PbFormState) => {
    const id = translationIds?.[model];
    if (!contentTranslations || !id) return null;
    return (
      <TranslationHint dark={dark} original={f[field] as string | undefined}
        entries={contentTranslations[model]?.[field as string]} target={{ type: model, id, field: field as string }} />
    );
  };
  const readOnlyText = (field: 'hobbys' | 'umfeld' | 'sonstiges') => {
    const id = translationIds?.profile;
    if (!contentTranslations || !id) return <PlainText dark={dark} value={f[field]} />;
    return <TranslatedText dark={dark} original={f[field]} entries={contentTranslations.profile?.[field]}
      target={{ type: 'profile', id, field }} />;
  };

  // Sektionen 02–07: bei Bedarf per Schalter aktivieren (s. sectionToggles.ts).
  const toggle = (id: ToggleSectionId, title: string): SectionToggle => ({
    value: isSectionActive(f, id),
    onChange: v => set('sektionen_aktiv', { ...(f.sektionen_aktiv ?? {}), [id]: v }),
    label: tr('pflege.form.sektion.toggle', { title }),
    offHint: tr('pflege.form.sektion.off_hint'),
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* 00 · Profil (Familienkonto-Rolle) — Pflegegrad hier ausgeblendet,
          um Dopplung mit der Pflege-Sektion zu vermeiden. */}
      {showProfilHead && (
        <ProfilHeadSection
          f={f}
          set={set}
          opts={opts}
          dark={dark}
          showPflegegrad={false}
          profiltypOptions={labelOptions('profiltyp', opts, f.profiltyp)}
          familiengradOptions={labelOptions('familiengrad', opts)}
          invalidFields={invalidFields}
        />
      )}

      {/* 01 · Stammdaten (inkl. Adresse & Kontakt) */}
      <Section dark={dark} id="stammdaten" num="01" title={tr('pflege.form.section.stammdaten.title')} desc={tr('pflege.form.section.stammdaten.desc')}>
        <Grid cols={4}>
          <Field dark={dark} label={fl('anrede')} required={req('anrede')} hasError={isInvalid('anrede')}><Select dark={dark} {...b('anrede')} placeholder="—" options={HARDCODED.anrede} hasError={isInvalid('anrede')} /></Field>
          <Field dark={dark} label={fl('titel')}><Text dark={dark} {...b('titel')} placeholder={tr('pflege.form.ph.titel')} />{hint('profile', 'titel')}</Field>
          <Field dark={dark} label={fl('vorname')} required={req('vorname')} hasError={isInvalid('vorname')}><Text dark={dark} {...b('vorname')} placeholder={tr('pflege.form.ph.vorname')} hasError={isInvalid('vorname')} /></Field>
          <Field dark={dark} label={fl('nachname')} required={req('nachname')} hasError={isInvalid('nachname')}><Text dark={dark} {...b('nachname')} placeholder={tr('pflege.form.ph.nachname')} hasError={isInvalid('nachname')} /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('geburtsdatum')}><Text dark={dark} {...b('geburtsdatum')} type="date" /></Field>
            <Field dark={dark} label={fl('nationalitaet')}><Select dark={dark} {...b('nationalitaet')} placeholder="—" options={nationalitaetOptions(f.nationalitaet)} /></Field>
          </Grid>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={1}>
            <Field dark={dark} label={fl('familienstand')} required={req('familienstand')} hasError={isInvalid('familienstand')}><Select dark={dark} {...b('familienstand')} placeholder="—" options={opt('familienstand')} hasError={isInvalid('familienstand')} /></Field>
          </Grid>
        </div>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.kontakt')}</GroupLabel>
        <Grid cols={3}>
          <Field dark={dark} label={fl('email')} hint={tr('pflege.form.hint.unique')}><Text dark={dark} {...b('email')} type="email" placeholder="name@beispiel.de" /></Field>
          <Field dark={dark} label={fl('mobile')} hint={tr('pflege.form.hint.unique')}><PhoneInput dark={dark} {...b('mobile')} ariaLabel={fl('mobile')} placeholder="170 1234567" /></Field>
          <Field dark={dark} label={fl('telefon')} hint={tr('pflege.form.hint.festnetz')} {...rq('telefon')}><PhoneInput dark={dark} {...b('telefon')} ariaLabel={fl('telefon')} placeholder="89 1234567" hasError={isInvalid('telefon')} /></Field>
        </Grid>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.anschrift')}</GroupLabel>
        <Grid cols={6}>
          <Field dark={dark} label={fl('strasse')} span={4}><Text dark={dark} {...b('strasse')} placeholder={tr('pflege.form.ph.strasse')} /></Field>
          <Field dark={dark} label={fl('hausnummer')} span={2}><Text dark={dark} {...b('hausnummer')} placeholder="12a" /></Field>
          <Field dark={dark} label={fl('plz')} span={2}><Text dark={dark} {...b('plz')} placeholder="80331" /></Field>
          <Field dark={dark} label={fl('ort')} span={2}><Text dark={dark} {...b('ort')} placeholder={tr('pflege.form.ph.ort')} /></Field>
          <Field dark={dark} label={fl('land')} span={2} required={req('land')} hasError={isInvalid('land')}><Select dark={dark} {...b('land')} placeholder="—" options={HARDCODED.land} hasError={isInvalid('land')} /></Field>
        </Grid>
      </Section>

      {/* A9: schlanke Sicht endet hier. */}
      {slim ? null : (<>

      {/* 02 · Versicherungsdaten — WP5: gated by pb.section.versicherung.* */}
      {perms.versicherung.canRead && (
        <ReadOnlyWrapper canWrite={perms.versicherung.canWrite}>
          <Section dark={dark} id="versicherung" num="02" title={tr('pflege.form.section.versicherung.title')} desc={tr('pflege.form.section.versicherung.desc')} toggle={toggle('versicherung', tr('pflege.form.section.versicherung.title'))}>
            <Grid cols={2}>
              <Field dark={dark} label={fl('krankenkasse')} {...rq('krankenkasse')}><Text dark={dark} {...b('krankenkasse')} placeholder={tr('pflege.form.ph.krankenkasse')} />{hint('profile', 'krankenkasse')}</Field>
              <Field dark={dark} label={fl('krankenkasse_typ')} {...rq('krankenkasse_typ')}>
                <Select dark={dark} {...b('krankenkasse_typ')} placeholder="—" options={HARDCODED.krankenkasse_typ} />
              </Field>
            </Grid>
            <GroupLabel dark={dark}>{tr('pflege.form.glabel.kennnummern')}</GroupLabel>
            <Grid cols={3}>
              <Field dark={dark} label={fl('kranken_vnr')} {...rq('kranken_vnr')}><Text dark={dark} {...b('kranken_vnr')} placeholder="A123456789" /></Field>
              <Field dark={dark} label={fl('sozial_vnr')} {...rq('sozial_vnr')}><Text dark={dark} {...b('sozial_vnr')} placeholder="65 120366 M 123" /></Field>
              <Field dark={dark} label={fl('renten_vnr')} {...rq('renten_vnr')}><Text dark={dark} {...b('renten_vnr')} placeholder="12 120366 M 123" /></Field>
              <Field dark={dark} label={fl('steuer_id')} {...rq('steuer_id')}><Text dark={dark} {...b('steuer_id')} placeholder="12 345 678 901" /></Field>
              <Field dark={dark} label={fl('lbnr')} {...rq('lbnr')}><Text dark={dark} {...b('lbnr')} placeholder="LB-00482" /></Field>
            </Grid>
            <div style={{ marginTop: 16 }}>
              <ToggleRow dark={dark} {...bBool('sozialamt_aufstockung')}
                label={fl('sozialamt_aufstockung')}
                desc={tr('pflege.form.toggle.sozialamt_desc')} />
            </div>
          </Section>
        </ReadOnlyWrapper>
      )}

      {/* 03 · Pflege-Parameter */}
      <Section dark={dark} id="pflege" num="03" title={tr('pflege.form.section.pflege.title')} desc={tr('pflege.form.section.pflege.desc')} toggle={toggle('pflege', tr('pflege.form.section.pflege.title'))}>
        <Grid cols={3}>
          <Field dark={dark} label={fl('groesse')} {...rq('groesse')}><Text dark={dark} {...b('groesse')} type="number" suffix="cm" placeholder="170" /></Field>
          <Field dark={dark} label={fl('gewicht')} {...rq('gewicht')}><Text dark={dark} {...b('gewicht')} type="number" suffix="kg" placeholder="72" /></Field>
          <Field dark={dark} label={fl('raucher')} required={req('raucher')} hasError={isInvalid('raucher')}><Select dark={dark} {...b('raucher')} placeholder="—" options={opt('raucher')} hasError={isInvalid('raucher')} /></Field>
        </Grid>
        <div style={{ marginTop: 16 }}>
        <Grid cols={2}>
          <Field dark={dark} label={fl('pflegegrad')} required={req('pflegegrad')} hasError={isInvalid('pflegegrad')}><Select dark={dark} {...b('pflegegrad')} placeholder="—" options={opt('pflegegrad')} hasError={isInvalid('pflegegrad')} /></Field>
          <Field dark={dark} label={fl('beweglichkeit')} required={req('beweglichkeit')} hasError={isInvalid('beweglichkeit')}>
            <Select dark={dark} {...b('beweglichkeit')} placeholder="—" options={opt('beweglichkeit')} hasError={isInvalid('beweglichkeit')} />
          </Field>
        </Grid>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('einschraenkungen')} {...rq('einschraenkungen')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('einschraenkungen')} cols={3} options={opt('einschraenkungen')} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('sonstige_einschraenkungen')} {...rq('sonstige_einschraenkungen')}><Text dark={dark} {...b('sonstige_einschraenkungen')} placeholder={tr('pflege.form.ph.freetext')} />{hint('profile', 'sonstige_einschraenkungen')}</Field>
            <Field dark={dark} label={fl('besserung')} required={req('besserung')} hasError={isInvalid('besserung')}><Select dark={dark} {...b('besserung')} placeholder="—" options={opt('besserung')} hasError={isInvalid('besserung')} /></Field>
          </Grid>
        </div>
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('sinneswahrnehmung')} {...rq('sinneswahrnehmung')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('sinneswahrnehmung')} cols={2} options={opt('sinneswahrnehmung')} />
          </Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <Grid cols={2}>
            <Field dark={dark} label={fl('geisteszustand')} required={req('geisteszustand')} hasError={isInvalid('geisteszustand')}><Select dark={dark} {...b('geisteszustand')} placeholder="—" options={opt('geisteszustand')} hasError={isInvalid('geisteszustand')} /></Field>
            <Field dark={dark} label={fl('schlafverhalten')} required={req('schlafverhalten')} hasError={isInvalid('schlafverhalten')}><Select dark={dark} {...b('schlafverhalten')} placeholder="—" options={opt('schlafverhalten')} hasError={isInvalid('schlafverhalten')} /></Field>
          </Grid>
        </div>
        <GroupLabel dark={dark}>{tr('pflege.form.glabel.support')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('grundpflege')} required={req('grundpflege')} hasError={isInvalid('grundpflege')}><Select dark={dark} {...b('grundpflege')} placeholder="—" options={opt('grundpflege')} hasError={isInvalid('grundpflege')} /></Field>
          <Field dark={dark} label={fl('hygiene')} required={req('hygiene')} hasError={isInvalid('hygiene')}><Select dark={dark} {...b('hygiene')} placeholder="—" options={opt('hygiene')} hasError={isInvalid('hygiene')} /></Field>
          <Field dark={dark} label={fl('ankleiden')} required={req('ankleiden')} hasError={isInvalid('ankleiden')}><Select dark={dark} {...b('ankleiden')} placeholder="—" options={opt('ankleiden')} hasError={isInvalid('ankleiden')} /></Field>
          <Field dark={dark} label={fl('essen')} required={req('essen')} hasError={isInvalid('essen')}><Select dark={dark} {...b('essen')} placeholder="—" options={opt('essen')} hasError={isInvalid('essen')} /></Field>
          <Field dark={dark} label={fl('urinkontrolle')} required={req('urinkontrolle')} hasError={isInvalid('urinkontrolle')}><Select dark={dark} {...b('urinkontrolle')} placeholder="—" options={opt('urinkontrolle')} hasError={isInvalid('urinkontrolle')} /></Field>
          <Field dark={dark} label={fl('stuhlkontrolle')} required={req('stuhlkontrolle')} hasError={isInvalid('stuhlkontrolle')}><Select dark={dark} {...b('stuhlkontrolle')} placeholder="—" options={opt('stuhlkontrolle')} hasError={isInvalid('stuhlkontrolle')} /></Field>
        </Grid>

        {/* Pflegedienst (SGB V) — aus der eigenen Sektion 04 hierher verschoben. */}
        <GroupLabel dark={dark}>{tr('pflege.form.section.pflegedienst.title')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('sgb5')} {...rq('sgb5')}>
            <Select
              dark={dark}
              {...b('sgb5')}
              placeholder={tr('pflege.form.sgb5.please_set')}
              options={[
                { value: 'benoetigt', label: tr('pflege.form.sgb5.required') },
                { value: 'nicht_benoetigt', label: tr('pflege.form.sgb5.not_required') },
              ]}
            />
          </Field>
        </Grid>
        {f.sgb5 === 'benoetigt' && (
          <>
            <div style={{ marginTop: 16 }}>
              <ToggleRow
                dark={dark}
                {...bBool('sgb5_medigabe')}
                label={fl('sgb5_medigabe')}
                desc={tr('pflege.form.sgb5.medigabe_desc')}
              />
            </div>
            {f.sgb5_medigabe && (
              <div style={{ marginTop: 16 }}>
                <Grid cols={2}>
                  <Field dark={dark} label={fl('sgb5_medigabe_taeglich')}>
                    <Range dark={dark} {...b('sgb5_medigabe_taeglich')} min={1} max={10} step={1} />
                  </Field>
                </Grid>
              </div>
            )}
            <div style={{ marginTop: 16 }}>
              <ToggleRow
                dark={dark}
                {...bBool('sgb5_ats')}
                label={fl('sgb5_ats')}
                desc={tr('pflege.form.sgb5.ats_desc')}
              />
            </div>
          </>
        )}
      </Section>

      {/* 04 · Charaktereigenschaften (ehem. Platz der SGB5-Sektion) — Werte
          kommen vom Familienkonto und sind hier je Person pflegbar. */}
      <Section dark={dark} id="charakter" num="04" title={tr('pflege.form.section.charakter.title')} desc={tr('pflege.form.section.charakter.desc')} toggle={toggle('charakter', tr('pflege.form.section.charakter.title'))}>
        {/* Wohnsituation / Wohnort / Einkaufsmoeglichkeiten sind hier bewusst
            NICHT mehr enthalten: sie werden auf Kontoebene unter
            „Familienkonto bearbeiten" gepflegt (WohnenForm.tsx) und waren hier
            eine Dublette. */}
        <div>
          <ToggleRow
            dark={dark}
            {...bBool('ausgepraegtes_familienleben')}
            label={fl('ausgepraegtes_familienleben')}
            desc={tr('pflege.form.hint.familienleben')}
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <Field dark={dark} label={fl('charaktereigenschaften')} {...rq('charaktereigenschaften')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('charaktereigenschaften')} cols={3} options={opt('charaktereigenschaften')} />
          </Field>
        </div>
      </Section>

      {/* 05 · Wunsch-BK */}
      <Section dark={dark} id="wunsch" num="05" title={tr('pflege.form.section.wunsch.title')} desc={tr('pflege.form.section.wunsch.desc')} toggle={toggle('wunsch', tr('pflege.form.section.wunsch.title'))}>
        <Field dark={dark} label={fl('bk_anrede')} {...rq('bk_anrede')} hint={tr('pflege.form.hint.multi')}>
          <CheckGroup dark={dark} {...bMulti('bk_anrede')} cols={3} options={opt('bk_anrede')} />
        </Field>
        {/* „BK Wohnraum" (bk_wohnen) wird seit 23.10.2026 am Familienkonto
            gepflegt (WohnenForm.tsx) und hier bewusst nicht mehr angeboten. */}
        <div style={{ marginTop: 18 }}>
          <Field dark={dark} label={fl('bk_aktivitaeten')} {...rq('bk_aktivitaeten')} hint={tr('pflege.form.hint.multi')}>
            <CheckGroup dark={dark} {...bMulti('bk_aktivitaeten')} cols={2} options={opt('bk_aktivitaeten')} />
          </Field>
        </div>
        <div style={{ marginTop: 18 }}>
          <Grid cols={3}>
            <Field dark={dark} label={fl('bk_deutsch')} {...rq('bk_deutsch')}><Select dark={dark} {...b('bk_deutsch')} placeholder="—" options={opt('bk_deutsch')} /></Field>
            <Field dark={dark} label={fl('bk_fuehrerschein')} {...rq('bk_fuehrerschein')}><Select dark={dark} {...b('bk_fuehrerschein')} placeholder="—" options={opt('bk_fuehrerschein')} /></Field>
            {/* War als schema_only deaktiviert und liess sich deshalb nie
                setzen — das Feld steuert aber die BK-Auswahl. */}
            <Field dark={dark} label={fl('bk_raucher')} required={req('bk_raucher')} hasError={isInvalid('bk_raucher')}>
              <Select dark={dark} {...b('bk_raucher')} placeholder={tr('pflege.form.ph.egal')} options={opt('bk_raucher')} hasError={isInvalid('bk_raucher')} />
            </Field>
          </Grid>
        </div>
        {/* Kategorie (Badge) steht seit Ticket cmv58ofmr0009ry01i0wefm94 hier
            statt in 10 · Status. */}
        <div style={{ marginTop: 18 }}>
          <Grid cols={3}>
            {/* Pflichtfeld bei aktiver Sektion: „Kein Status" (0) ist hier keine
                Auswahl, ein leerer/0-Wert zeigt den Platzhalter. */}
            <Field dark={dark} label={fl('badge')} {...rq('badge')}>
              <Select dark={dark} {...b('badge')} value={f.badge === '0' ? '' : f.badge} placeholder="—"
                options={HARDCODED.badge.filter(o => o.value !== '0')} hasError={isInvalid('badge')} />
            </Field>
          </Grid>
        </div>
      </Section>

      {/* 06 · Freitexte */}
      <Section dark={dark} id="freitexte" num="06" title={tr('pflege.form.section.freitexte.title')} desc={tr('pflege.form.section.freitexte.desc')} toggle={toggle('freitexte', tr('pflege.form.section.freitexte.title'))}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {freitexteReadOnly ? (
            <>
              <Field dark={dark} label={fl('hobbys')} {...rq('hobbys')}>{readOnlyText('hobbys')}</Field>
              <Field dark={dark} label={fl('umfeld')} {...rq('umfeld')}>{readOnlyText('umfeld')}</Field>
              <Field dark={dark} label={fl('sonstiges')} {...rq('sonstiges')}>{readOnlyText('sonstiges')}</Field>
            </>
          ) : (
            <>
              <Field dark={dark} label={fl('hobbys')} {...rq('hobbys')}><Area dark={dark} {...b('hobbys')} rows={2} placeholder={tr('pflege.form.ph.hobbys')} />{hint('profile', 'hobbys')}</Field>
              <Field dark={dark} label={fl('umfeld')} {...rq('umfeld')}><Area dark={dark} {...b('umfeld')} rows={2} placeholder={tr('pflege.form.ph.umfeld')} />{hint('profile', 'umfeld')}</Field>
              <Field dark={dark} label={fl('sonstiges')} {...rq('sonstiges')}><Area dark={dark} {...b('sonstiges')} rows={2} placeholder={tr('pflege.form.ph.sonstiges')} />{hint('profile', 'sonstiges')}</Field>
            </>
          )}
        </div>
      </Section>

      {/* 07 · SIS — Strukturiertes Informationssystem (9 Themenfelder) */}
      <Section dark={dark} id="sis" num="07" title={tr('pflege.form.section.sis.title')} desc={tr('pflege.form.section.sis.desc')} toggle={toggle('sis', tr('pflege.form.section.sis.title'))}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map(n => (
            <Field key={n} dark={dark} label={fl(`freitext_sis_${n}`)} {...rq(`freitext_sis_${n}` as keyof PbFormState)}>
              <Area dark={dark} {...b(`freitext_sis_${n}` as keyof PbFormState)} rows={2} placeholder={tr(`pflege.form.ph.freitext_sis_${n}`)} />
              {hint('profile', `freitext_sis_${n}` as keyof PbFormState)}
            </Field>
          ))}
        </div>
      </Section>

      {/* 08 · Termine — Anamnese-Termin/Beratungsgespräch (editierbar, sofort-
          speichernd wie PhaseRulesBlock) + Wiedervorlage (nur lesend).
          Rendert nur mit `profileId` — im Familienkonto-„Person hinzufügen"
          (noch keine Person angelegt) gibt es nichts zu speichern. */}
      <TermineSection f={f} dark={dark} profileId={profileId} onSaveTermin={onSaveTermin} />

      {/* 09 · Vertrag & Preise — WP5: gated by pb.section.vertrag.* */}
      {perms.vertrag.canRead && (
        <ReadOnlyWrapper canWrite={perms.vertrag.canWrite}>
          <Section dark={dark} id="vertrag" num="09" title={tr('pflege.form.section.vertrag.title')} desc={tr('pflege.form.section.vertrag.desc')}>
            <Grid cols={2}>
              <Field dark={dark} label={fl('wage')} hint={tr('pflege.form.hint.table_users')}><Text dark={dark} {...b('wage')} type="number" prefix="€" placeholder="2100" /></Field>
            </Grid>
            {/* Vertragsanpassung + Ausgleichsbetrag wie in CRM 1.0: eigene
                Datensätze (prices_pb / price_bonus) mit eigenem Button, nicht
                Teil des Profil-Saves. Braucht einen bestehenden User. */}
            {priceUser?.id != null && (
              <PbVertragBlock dark={dark} user={priceUser} blocked={Boolean(f.todestag)} />
            )}
          </Section>
        </ReadOnlyWrapper>
      )}

      {/* 10 · Status — WP5: gated by pb.section.status.* */}
      {perms.status.canRead && (
        <ReadOnlyWrapper canWrite={perms.status.canWrite}>
          <Section dark={dark} id="status" num="10" title={tr('pflege.form.section.status.title')} desc={tr('pflege.form.section.status.desc')}>
            <Grid cols={3}>
              <Field dark={dark} label={fl('rating')} hint={tr('pflege.form.hint.readonly')}>
                <div style={{ paddingTop: 8 }}><Rating dark={dark} value={f.rating ?? 0} /></div>
              </Field>
            </Grid>
            <GroupLabel dark={dark}>{tr('pflege.form.glabel.pause_death')}</GroupLabel>
            <Grid cols={1}>
              <Field dark={dark} label={fl('todestag')} hint={tr('pflege.form.hint.with_care')}>
                <Text dark={dark} {...b('todestag')} type="date" />
              </Field>
            </Grid>
            <div style={{ marginTop: 16 }}>
              <ToggleRow dark={dark} {...bBool('invisible')} label={fl('invisible')}
                desc={tr('pflege.form.toggle.invisible_desc')} />
            </div>
          </Section>
        </ReadOnlyWrapper>
      )}

      {/* 11 · Bank — WP5: gated by pb.section.bank.* */}
      {perms.bank.canRead && (
        <ReadOnlyWrapper canWrite={perms.bank.canWrite}>
          <Section dark={dark} id="bank" num="11" title={tr('pflege.form.section.bank.title')} desc={tr('pflege.form.section.bank.desc')}>
            <Grid cols={2}>
              <Field dark={dark} label={fl('kontoinhaber')}><Text dark={dark} {...b('kontoinhaber')} placeholder={tr('pflege.form.ph.kontoinhaber')} /></Field>
              <Field dark={dark} label={fl('bankname')}><Text dark={dark} {...b('bankname')} placeholder={tr('pflege.form.ph.bankname')} />{hint('user', 'bankname')}</Field>
              <Field dark={dark} label={fl('iban')} span={2}><Text dark={dark} {...b('iban')} placeholder="DE00 0000 0000 0000 0000 00" /></Field>
            </Grid>
          </Section>
        </ReadOnlyWrapper>
      )}

      {/* 12 · Sonstiges */}
      {/* Gruppe, Sprache, Deutschkenntnisse und Freitext (description) sind
          hier bewusst entfernt — nur noch die System-Infos. */}
      <Section dark={dark} id="sonstiges" num="12" title={tr('pflege.form.section.sonstiges.title')} desc={tr('pflege.form.section.sonstiges.desc')}>
        <GroupLabel dark={dark} top>{tr('pflege.form.glabel.system')}</GroupLabel>
        <Grid cols={2}>
          <Field dark={dark} label={fl('created_by')}><ReadOnly dark={dark} value={f.created_by_name || f.created_by} /></Field>
          <Field dark={dark} label={fl('origin')}><ReadOnly dark={dark} value={f.origin} /></Field>
        </Grid>
      </Section>

      </>)}

    </div>
  );
}

/**
 * 08 · Termine — Nacharbeit: für Pflegeempfänger in der letzten Stage gab es
 * keine Stelle mehr, `anamnese_termin_am`/`beratungsgespraech_am` zu setzen,
 * weil der PhaseRulesBlock nur Bedingungen der NÄCHSTEN Stage zeigt (s.
 * PhaseRulesBlock.tsx). Diese Sektion macht beide Termine unabhängig von
 * Phasen-Bedingungen pflegbar.
 *
 * Speichert wie PhaseRulesBlock sofort pro Feld über den v2-Profil-Save
 * (PUT /v2/admin/profiles/{id}, hier `onSaveTermin`) — NICHT über den
 * Profildaten-Save-Button, damit ein Klick auf „Profil speichern" mit
 * veraltetem Formularstand den gerade gesetzten Termin nicht überschreibt
 * (dasselbe Muster wie `assigned_to`, s. fields.ts USER_FIELDS-Kommentar).
 * Genau deshalb braucht es `profileId` + `onSaveTermin` von außen, statt den
 * großen `set()`/Save-Payload-Weg zu nutzen.
 *
 * „Wiedervorlage" ist bewusst nur lesend: sie wird über eine eigene Maske
 * (PATCH /v2/admin/users/{id}/wiedervorlage) mit Uhrzeit gepflegt, s. v1.ts.
 */
function TermineSection({
  f, dark, profileId, onSaveTermin,
}: {
  f: PbFormState;
  dark: boolean;
  profileId?: number | null;
  onSaveTermin?: (patch: Partial<Record<TerminField, string | null>>) => Promise<void>;
}) {
  const { t: tr } = useTranslation();
  const [savingField, setSavingField] = useState<TerminField | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Ohne profileId (z. B. „Person hinzufügen" im Familienkonto — die Person
  // existiert noch nicht) gibt es nichts zu speichern.
  if (!profileId || !onSaveTermin) return null;

  const stored = (field: TerminField): string => {
    const raw = (f as Record<string, unknown>)[field];
    return field.endsWith('_um') ? toTimeInput(raw) : (typeof raw === 'string' ? raw : '');
  };

  // Datum + Uhrzeit in EINEM Feld; gespeichert wird in beide Spalten
  // (Datum zuerst, dann Uhrzeit). 00:00 = ganztägig (Uhrzeit NULL).
  const commitDateTime = async (dateField: TerminDateField, value: string) => {
    const timeField = TERMIN_TIME_FIELDS[dateField];
    const { date, time } = splitDateTime(value);
    if (savingField) return;
    const patch: Partial<Record<TerminField, string | null>> = {};
    if (date !== (stored(dateField) || null)) patch[dateField] = date;
    if (time !== (stored(timeField) || null)) patch[timeField] = time;
    if (Object.keys(patch).length === 0) return;
    setError(null);
    setSavingField(dateField);
    try {
      await onSaveTermin(patch);
    } catch {
      setError(tr('pflege.edit.save_failed'));
    } finally {
      setSavingField(null);
    }
  };

  const terminField = (dateField: TerminDateField) => {
    const label = fieldLabel(tr, dateField);
    const timeField = TERMIN_TIME_FIELDS[dateField];
    return (
      <Field dark={dark} label={label} hint={tr('pflege.form.hint.all_day_midnight')}>
        <DateTimeInput
          ariaLabel={label}
          date={stored(dateField)}
          time={stored(timeField)}
          readOnly={savingField === dateField}
          onCommit={v => { void commitDateTime(dateField, v); }}
          style={{ ...terminInputStyle(dark), width: '100%', boxSizing: 'border-box' }}
        />
      </Field>
    );
  };

  return (
    <Section
      dark={dark}
      id="termine"
      num="08"
      title={tr('pflege.form.section.termine.title')}
      desc={tr('pflege.form.section.termine.desc')}
    >
      <Grid cols={3}>
        {terminField('anamnese_termin_am')}
        {terminField('beratungsgespraech_am')}
        <Field dark={dark} label={fieldLabel(tr, 'wiedervorlage_am')} hint={tr('pflege.form.hint.readonly')}>
          <ReadOnly dark={dark} value={formatDate(f.wiedervorlage_am, '')} />
        </Field>
      </Grid>
      {error && <div style={{ fontSize: 12, color: '#b42318', marginTop: 8 }}>{error}</div>}
    </Section>
  );
}

/** Eingabestil wie `Text` (controls.tsx) — hier natives <input> wegen onBlur. */
function terminInputStyle(dark: boolean): React.CSSProperties {
  const t = getThemeColors(dark);
  return {
    border: `1px solid ${t.border}`, borderRadius: 8, padding: '9px 12px',
    fontSize: 13.5, fontFamily: 'Outfit,sans-serif', color: t.heading,
    background: t.inputBg, outline: 'none', boxSizing: 'border-box',
  };
}

/* ─── 09 · Vertragsanpassung + Ausgleichsbetrag (wie CRM 1.0) ─────────────
 *
 * Vertragsanpassung: legt je Absenden eine NEUE prices_pb-Zeile an
 * (POST /v1/admin/user/{id}/price) — gültig ab Monatsanfang, das Backend
 * setzt die jeweils gültige Zeile aktiv. Vorbelegt mit dem Folgemonat und
 * dem aktuellen Vertrag (CRM 1.0 ProfileData.js).
 *
 * Ausgleichsbetrag: einmaliger Betrag für einen Monat, eigene Tabelle
 * price_bonus (POST /v1/admin/user/{id}/bonus). Fließt in der Masterliste in
 * den Rechnungspreis des Monats ein (Care_Export_Sheet_Pbs). Mehrere Einträge
 * im selben Monat: der zuletzt angelegte gilt (nicht die Summe).
 *
 * Wie in CRM 1.0 lassen sich bei gesetztem Todes-/Kündigungstag keine neuen
 * Einträge mehr anlegen.
 */
type Row = Record<string, unknown>;

const monthKey = (v: unknown): string => (v == null ? '' : String(v).slice(0, 7));
const monthOffset = (offset: number): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() + offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** Maßgeblicher Ausgleichsbetrag-Eintrag eines Monats (YYYY-MM) — bei mehreren der zuletzt angelegte. */
export function bonusEntryForMonth(boni: Row[], month: string): Row | null {
  const hits = boni.filter(b => monthKey(b.gueltig_fuer) === month);
  if (hits.length === 0) return null;
  const newer = (a: Row, b: Row): boolean => {
    const ca = String(a.created_at ?? '');
    const cb = String(b.created_at ?? '');
    return cb > ca || (cb === ca && Number(b.id ?? 0) > Number(a.id ?? 0));
  };
  return hits.reduce((a, b) => (newer(a, b) ? b : a));
}

/** Ausgleichsbetrag eines Monats (YYYY-MM) — bei mehreren gilt der zuletzt angelegte. */
export function bonusForMonth(boni: Row[], month: string): number | null {
  const latest = bonusEntryForMonth(boni, month);
  if (!latest) return null;
  const n = Number(latest.bonus);
  return Number.isFinite(n) ? n : null;
}

/** Kommentar zum maßgeblichen Ausgleichsbetrag eines Monats — oder '' wenn keiner. */
export function bonusKommentarForMonth(boni: Row[], month: string): string {
  return String(bonusEntryForMonth(boni, month)?.kommentar ?? '').trim();
}

const byMonthDesc = (key: string) => (a: Row, b: Row) => monthKey(b[key]).localeCompare(monthKey(a[key]));

function PbVertragBlock({ dark, user, blocked }: { dark: boolean; user: V1User; blocked: boolean }) {
  const { t: tr } = useTranslation();
  const t = getThemeColors(dark);
  const contractLabel = (v: unknown) =>
    HARDCODED.vertrag.find(o => o.value === v)?.label ?? (v == null || v === '' ? '—' : String(v));

  // Historien kommen vom User; nach eigenem Anlegen gilt die Antwort des
  // Endpunkts, bis ein neuer User-Stand hereinkommt (Reset beim Rendern statt
  // per Effect, s. react.dev „Adjusting state when a prop changes").
  const [pricesOverride, setPrices] = useState<Row[] | null>(null);
  const [boniOverride, setBoni] = useState<Row[] | null>(null);
  // Aktueller Vertrag nach eigenem Speichern (Antwort des Endpunkts), damit
  // „Aktueller Stand" sofort den neuen Preis zeigt. undefined = vom User.
  const [currentOverride, setCurrentOverride] = useState<Row | null | undefined>(undefined);
  const [seenUser, setSeenUser] = useState(user);
  if (seenUser !== user) {
    setSeenUser(user);
    setPrices(null);
    setBoni(null);
    setCurrentOverride(undefined);
  }
  const prices: Row[] = pricesOverride ?? (Array.isArray(user.prices) ? user.prices : []);
  const boni: Row[] = boniOverride ?? (Array.isArray(user.boni) ? user.boni as Row[] : []);

  const current = currentOverride !== undefined ? currentOverride : (user.priceCurrent ?? null);
  const [gueltigAb, setGueltigAbRaw] = useState<string>(() => monthOffset(1));
  const [vertrag, setVertragRaw] = useState<string>(() => String(current?.vertrag ?? ''));
  const [preis, setPreisRaw] = useState<string>(() => (current?.vertragspreis != null ? String(current.vertragspreis) : ''));
  // Hat der Nutzer die Vertragsanpassung angefasst und noch nicht gespeichert?
  // Nur dann nimmt „Profil speichern" sie mit (s. pendingVertrag.ts).
  const [priceTouched, setPriceTouched] = useState(false);
  const setGueltigAb = (v: string) => { setGueltigAbRaw(v); setPriceTouched(true); };
  const setVertrag = (v: string) => { setVertragRaw(v); setPriceTouched(true); };
  const setPreis = (v: string) => { setPreisRaw(v); setPriceTouched(true); };
  const [gueltigFuer, setGueltigFuer] = useState<string>(() => monthOffset(0));
  const [bonus, setBonus] = useState<string>('');
  const [bonusKommentar, setBonusKommentar] = useState<string>('');
  const [busy, setBusy] = useState<'price' | 'bonus' | null>(null);
  const [priceMsg, setPriceMsg] = useState<{ error: string | null; ok: string | null }>({ error: null, ok: null });
  const [bonusMsg, setBonusMsg] = useState<{ error: string | null; ok: string | null }>({ error: null, ok: null });

  /** Speichert die Vertragsanpassung; wirft bei unvollständiger Eingabe oder API-Fehler. */
  const savePrice = async () => {
    if (!gueltigAb || !vertrag || preis === '') {
      const msg = tr('pflege.form.vertrag.missing');
      setPriceMsg({ error: msg, ok: null });
      throw new Error(msg);
    }
    setBusy('price');
    setPriceMsg({ error: null, ok: null });
    try {
      const res = await v1Users.createPrice(user.id, { gueltig_ab: gueltigAb, vertrag, vertragspreis: preis }) as
        { prices?: Row[]; priceCurrent?: Row | null } | undefined;
      const fresh = res?.prices;
      setPrices(Array.isArray(fresh) ? fresh : [{ gueltig_ab: gueltigAb, vertrag, vertragspreis: preis }, ...prices]);
      // „Aktueller Stand" sofort nachziehen: Antwort des Endpunkts, sonst der
      // neue Eintrag, sofern er schon gilt (Gültig-ab-Monat nicht in der Zukunft).
      if (res && 'priceCurrent' in res) {
        setCurrentOverride(res.priceCurrent ?? null);
      } else if (gueltigAb <= monthOffset(0)) {
        setCurrentOverride({ gueltig_ab: gueltigAb, vertrag, vertragspreis: preis });
      }
      setPriceTouched(false);
      setPriceMsg({ error: null, ok: tr('pflege.form.vertrag.saved') });
    } catch (e) {
      const msg = e instanceof Error ? e.message : tr('pflege.edit.save_failed');
      setPriceMsg({ error: msg, ok: null });
      throw new Error(msg);
    } finally {
      setBusy(null);
    }
  };

  const submitPrice = async () => {
    if (busy) return;
    try { await savePrice(); } catch { /* Meldung steht neben dem Knopf */ }
  };

  // „Profil speichern" nimmt eine angefasste, noch nicht gespeicherte
  // Vertragsanpassung mit (PbProfileEdit → flushPendingVertrag).
  const pendingRef = useRef<() => Promise<void>>(async () => {});
  pendingRef.current = async () => {
    if (blocked || !priceTouched || busy) return;
    await savePrice();
  };
  useEffect(() => registerPendingVertrag(user.id, () => pendingRef.current()), [user.id]);

  const submitBonus = async () => {
    if (busy) return;
    if (!gueltigFuer || bonus === '') {
      setBonusMsg({ error: tr('betreuung.form.bonus.missing'), ok: null });
      return;
    }
    setBusy('bonus');
    setBonusMsg({ error: null, ok: null });
    const gueltig = `${gueltigFuer}-01`;
    try {
      const kommentar = bonusKommentar.trim() || null;
      // v2 speichert den Kommentar mit (v1 createBonus verwarf ihn) und
      // liefert die vollständige Liste des Users zurück.
      const res = await v2PriceBonus.create(user.id, { gueltig_fuer: gueltig, bonus, kommentar });
      setBoni(Array.isArray(res?.boni) && res.boni.length > 0
        ? res.boni as Row[]
        : [{ gueltig_fuer: gueltig, bonus, kommentar, created_at: new Date().toISOString() }, ...boni]);
      setBonusMsg({ error: null, ok: tr('betreuung.form.bonus.saved') });
      setBonus('');
      setBonusKommentar('');
    } catch (e) {
      setBonusMsg({ error: priceBonusErrorMessage(e, tr('pflege.edit.save_failed')), ok: null });
    } finally {
      setBusy(null);
    }
  };

  const currentBonus = bonusForMonth(boni, monthOffset(0));
  const currentBonusKommentar = bonusKommentarForMonth(boni, monthOffset(0));
  const monthLabel = new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  return (
    <>
      {/* Aktueller Stand */}
      <div style={{
        marginTop: 16, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12,
        background: t.cardMuted, border: `1px solid ${t.borderSoft}`, borderRadius: 10, padding: '12px 14px',
      }}>
        <SummaryItem dark={dark} label={tr('pflege.form.field.vertrag')} value={contractLabel(current?.vertrag)} />
        <SummaryItem dark={dark} label={tr('pflege.form.field.vertragspreis')} value={current ? asEuro(current.vertragspreis) : '—'} />
        <SummaryItem dark={dark} label={tr('pflege.form.field.gueltig_ab')} value={current ? asMonth(current.gueltig_ab) : '—'} />
        <SummaryItem
          dark={dark}
          label={`${tr('pflege.form.field.ausgleichsbetrag')} (${monthLabel})`}
          value={currentBonus != null ? asEuro(currentBonus) : '—'}
          // Kommentar zum aktuellen Ausgleichsbetrag direkt in der Anzeige oben.
          note={currentBonus != null ? currentBonusKommentar : ''}
        />
      </div>

      {blocked && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: t.muted }}>{tr('pflege.form.vertrag.blocked')}</div>
      )}

      <GroupLabel dark={dark}>{tr('pflege.form.vertrag.title')}</GroupLabel>
      {!blocked && (
        <>
          <Grid cols={3}>
            <Field dark={dark} label={tr('pflege.form.field.gueltig_ab')}>
              <Text dark={dark} value={gueltigAb} onChange={setGueltigAb} type="month" />
            </Field>
            <Field dark={dark} label={tr('pflege.form.field.vertrag')}>
              <Select dark={dark} value={vertrag} onChange={setVertrag} placeholder="—" options={HARDCODED.vertrag} />
            </Field>
            <Field dark={dark} label={tr('pflege.form.field.vertragspreis')}>
              <Text dark={dark} value={preis} onChange={setPreis} type="number" prefix="€" suffix="/Mo." placeholder="3200" />
            </Field>
          </Grid>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" dark={dark} onClick={submitPrice} disabled={busy != null}>
              {busy === 'price' ? tr('pflege.edit.saving') : tr('pflege.form.vertrag.add')}
            </Button>
            <ActionStatus dark={dark} error={priceMsg.error} ok={priceMsg.ok} />
          </div>
          <div style={{ marginTop: 6, fontSize: 11.5, color: t.muted }}>{tr('pflege.form.vertrag.save_hint')}</div>
        </>
      )}
      {prices.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <HistoryTable
            dark={dark}
            columns={[
              tr('pflege.form.field.gueltig_ab'), tr('pflege.form.field.vertrag'),
              tr('pflege.form.field.vertragspreis'), tr('pflege.form.field.ausgleichsbetrag'),
            ]}
            rows={[...prices].sort(byMonthDesc('gueltig_ab')).map(p => {
              const b = bonusForMonth(boni, monthKey(p.gueltig_ab));
              return [asMonth(p.gueltig_ab), contractLabel(p.vertrag), asEuro(p.vertragspreis), b != null ? asEuro(b) : '—'];
            })}
          />
        </div>
      )}

      <GroupLabel dark={dark}>{tr('pflege.form.field.ausgleichsbetrag')}</GroupLabel>
      {!blocked && (
        <>
          <Grid cols={2}>
            <Field dark={dark} label={tr('betreuung.form.field.gueltig_fuer')}>
              <Text dark={dark} value={gueltigFuer} onChange={setGueltigFuer} type="month" />
            </Field>
            <Field dark={dark} label={tr('pflege.form.vertrag.bonus_once')}>
              <Text dark={dark} value={bonus} onChange={setBonus} type="number" prefix="€" placeholder="0" />
            </Field>
            {/* Kommentar — erscheint im Excel-Export in einer eigenen Spalte */}
            <Field dark={dark} label={tr('betreuung.form.field.bonus_kommentar')} span={2}>
              <Text dark={dark} value={bonusKommentar} onChange={setBonusKommentar} />
            </Field>
          </Grid>
          <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Button variant="secondary" size="sm" dark={dark} onClick={submitBonus} disabled={busy != null}>
              {busy === 'bonus' ? tr('pflege.edit.saving') : tr('betreuung.form.bonus.add')}
            </Button>
            <ActionStatus dark={dark} error={bonusMsg.error} ok={bonusMsg.ok} />
          </div>
        </>
      )}
      {boni.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <HistoryTable
            dark={dark}
            columns={[tr('betreuung.form.field.gueltig_fuer'), tr('pflege.form.field.ausgleichsbetrag'), tr('betreuung.form.field.bonus_kommentar')]}
            rows={[...boni].sort(byMonthDesc('gueltig_fuer')).map(b => [asMonth(b.gueltig_fuer), asEuro(b.bonus), String(b.kommentar ?? '') || '—'])}
          />
        </div>
      )}
    </>
  );
}

function SummaryItem({ dark, label, value, note }: { dark: boolean; label: string; value: string; note?: string }) {
  const t = getThemeColors(dark);
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 600, color: t.heading, marginTop: 3 }}>{value}</div>
      {note ? (
        <div data-testid="summary-note" style={{ fontSize: 12, color: t.text, marginTop: 3, lineHeight: 1.4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{note}</div>
      ) : null}
    </div>
  );
}

/** Kleiner Helfer, da TermineSection außerhalb von FormSections keinen Zugriff auf `fl()` hat. */
function fieldLabel(tr: (key: string) => string, key: string): string {
  return tr(`pflege.form.field.${key}`);
}
